import { Button, Combobox, Grid } from '@decisiv/ui-components';
import { t } from '@lingui/macro';
import _ from 'lodash';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import iconix from '@decisiv/iconix';
import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { FormGroup } from '../../pages/IntegrationDetailPage/StyledComponents';
import InvocationPayload from './InvocationPayload';
import ApplicationEventsApi from '../../api/application_events';
import ActionsApi from '../../api/actions';
import formatJsonText from '../../utils/formatJsonText';
import ConfiguredIntegrationsApi from '../../api/configured_integrations';

export default function LocationInfoTab({
  submitFunction,
  buttonLoading,
  integration,
}) {
  const [searchValue, setSearchValue] = useState('');
  const [dealerId, setDealerId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [srmAccountId, setSrmAccountId] = useState('');
  const [invocationInput, setInvocationInput] = useState('');
  const [originalSampleInput, setOriginalSampleInput] = useState('');
  const [configuredIntegrationId, setConfiguredIntegrationId] = useState('');
  const [selectedTriggerType, setSelectedTriggerType] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [requestPendingDispatch, setRequestPendingDispatch] = useState(false);

  const customEventPayload = {
    attributes: {
      schema: {
        example: {
          event: '',
          data: {},
        },
      },
    },
  };

  const handleSearchBoxChange = async (e) => {
    setSearchValue(e.target.value);
  };

  const setPayloads = (payload) => {
    setOriginalSampleInput(formatJsonText(JSON.stringify(payload)));

    setInvocationInput(formatJsonText(JSON.stringify(payload)));
  };

  const setCorrectDealerAndSRMOnInvocation = (srmId, dealer) => {
    if (invocationInput.length > 0) {
      const parsedInput = JSON.parse(invocationInput);
      if ('srm_account' in parsedInput.data && srmId) {
        parsedInput.data.srm_account = srmId;
      }

      if ('dealer_id' in parsedInput.data && dealer) {
        parsedInput.data.dealer_id = dealer;
      }

      setInvocationInput(formatJsonText(JSON.stringify(parsedInput)));
    }
  };

  const handleApplicationEventSamplePayload = (payload) => {
    // eslint-disable-next-line no-param-reassign
    delete payload.attributes.schema.example.metadata;
    // eslint-disable-next-line no-param-reassign
    payload.attributes.schema.example.data.srm_account = srmAccountId;
    // eslint-disable-next-line no-param-reassign
    payload.attributes.schema.example.data.dealer_id = dealerId;
    setPayloads(payload.attributes.schema.example);
  };

  const onSelectTriggerType = async (e) => {
    const triggerType = e.text;
    setInvocationInput('');
    setSelectedTriggerType(triggerType);
    setLoading(true);
    try {
      if (triggerType === 'http') {
        const responseActionDetails = await ActionsApi.fetchActionDetails(
          integration.attributes.action,
        );

        if (responseActionDetails.data) {
          setPayloads(
            responseActionDetails.data.attributes.schema.example.data
              .attributes,
          );
        }
      } else {
        const responseFetchEvent =
          await ApplicationEventsApi.fetchApplicationEventByName(triggerType);
        if (responseFetchEvent.data[0]) {
          handleApplicationEventSamplePayload(responseFetchEvent.data[0]);
        } else {
          customEventPayload.attributes.schema.example.event = triggerType;
          handleApplicationEventSamplePayload(customEventPayload);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const resetPayload = () => {
    setInvocationInput(originalSampleInput);
  };

  async function handleInputValueChange(event) {
    let value;

    if (event) {
      value = event.target.value;
    } else {
      value = '';
      setConfiguredIntegrationId('');
      setDealerId('');
      setSrmAccountId('');
      setLocationName('');
      setSearchResults([]);
    }
    setSearchValue(value);

    if (value.length > 1) {
      if (!requestPendingDispatch) {
        setRequestPendingDispatch(true);

        try {
          const searchConfiguredIntegration =
            await ConfiguredIntegrationsApi.searchConfiguredIntegrationByLocationSearchTerm(
              integration.id,
              event.target.value,
            );

          const results = [];
          searchConfiguredIntegration.data.forEach((result) => {
            results.push({
              label: `${result.attributes.name} - SRM Account Id: ${result.attributes.srm_account_id} - DealerID: ${result.attributes.decisiv_id}`,
              id: result.id,
              data: {
                name: result.attributes.name,
                srmAccountId: result.attributes.srm_account_id,
                dealerId: result.attributes.decisiv_id,
              },
            });
          });

          setSearchResults(results);
          setRequestPendingDispatch(false);
        } finally {
          setRequestPendingDispatch(false);
        }
      }
    }
  }

  function handleValueChange(selectedId) {
    const option = _.find(searchResults, (o) => o.id === selectedId);

    setSearchValue(option.label);
    setConfiguredIntegrationId(option.id);
    setDealerId(option.data.dealerId);
    setSrmAccountId(option.data.srmAccountId);
    setLocationName(option.data.name);
    setCorrectDealerAndSRMOnInvocation(
      option.data.srmAccountId,
      option.data.dealerId,
    );
  }

  useEffect(() => {
    if (selectedTriggerType) return;
    if (integration?.attributes?.trigger_type?.includes('http_api_request')) {
      onSelectTriggerType({ text: 'http' });
    } else {
      onSelectTriggerType({ text: integration?.attributes?.events[0] });
    }
  }, []);

  return (
    <Grid.Container>
      <Grid.Row>
        <Grid.Column span="4" style={{ minHeight: '800px' }}>
          <br />
          <FormGroup key="search-box">
            <Combobox
              label={t`Search Box`}
              name="search-box"
              inputValue={searchValue}
              hideLabel
              icon={iconix.Search}
              onChange={handleSearchBoxChange}
              loading={requestPendingDispatch}
              onChangeInputValue={handleInputValueChange}
              onChangeValue={handleValueChange}
              options={searchResults || undefined}
              variant="async"
            />
          </FormGroup>
          <FormGroup key="location-info-location-name">
            <TextField
              label={t`Location Name`}
              name="location-name"
              value={locationName}
              disabled
            />
          </FormGroup>
          <FormGroup key="location-info-srm-account-id">
            <TextField
              label={t`SRM Account ID`}
              name="srm-account-id"
              value={srmAccountId}
              disabled
            />
          </FormGroup>
          <FormGroup key="location-info-dealer-id">
            <TextField
              label={t`Dealer Id`}
              name="dealer-id"
              value={dealerId}
              disabled
            />
          </FormGroup>
          <FormGroup key="button">
            <Button
              aria-label={t`Run Manual Execution`}
              text={t`Run Manual Execution`}
              icon={iconix.ArrowRight}
              iconPosition="right"
              disabled={
                _.isEmpty(invocationInput) || _.isEmpty(configuredIntegrationId)
              }
              loading={buttonLoading}
              loadingText={t`Running`}
              onClick={() =>
                submitFunction(
                  invocationInput,
                  configuredIntegrationId,
                  selectedTriggerType,
                  srmAccountId,
                  dealerId,
                )
              }
            />
          </FormGroup>
        </Grid.Column>
        <InvocationPayload
          invocationInput={invocationInput}
          disableTriggerType={false}
          selectedTriggerType={selectedTriggerType}
          onSelectTriggerType={onSelectTriggerType}
          setInvocationInput={setInvocationInput}
          loading={loading}
          resetPayload={resetPayload}
          integration={integration}
        />
      </Grid.Row>
    </Grid.Container>
  );
}

LocationInfoTab.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  submitFunction: PropTypes.func.isRequired,
};
